import {useInfiniteQuery, useMutation, useQuery} from "@tanstack/react-query";
import type {Club, PlayerForm, Player} from '@iziping/common';
import { apiClient } from '@iziping/common';
import { ENDPOINTS, QUERY_CACHE } from '@/model/endpoints';
import type {ApiError, Page} from '@iziping/common';


export interface GetPlayersParam {
    pageNumber: number;
    pageSize: number;
}

interface usePlayerProps {
    params: GetPlayersParam;
}

export const useGetPlayer = (id?: number) => {
    return useQuery<Player>({
        queryKey: [QUERY_CACHE.PLAYER],
        queryFn: async () => {
            if (!id) throw new Error("ID manquant");
            const response = await apiClient.get<Player>(ENDPOINTS.player(id));
            return response.data;
        },
        enabled: !!id
    })
};

export const useSavePlayer = () => {
    return useMutation<Player, ApiError, { id?: number; data: PlayerForm }>({
        mutationFn: async ({ id, data }) => {
            if (id) {
                // update
                const response = await apiClient.put<Player>(`${ENDPOINTS.player(id)}`, data);
                return response.data;
            } else {
                // create
                const response = await apiClient.post<Player>(ENDPOINTS.players, data);
                return response.data;
            }
        },
    });
};


export const useGetPlayers = ({ params }: usePlayerProps) => {
    return useInfiniteQuery<Page<Player>, Error>({
        queryKey: ['players', params],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await apiClient.get<Page<Player>>(`${ENDPOINTS.players}`, {
                params: { ...params, pageNumber: pageParam },
            });
            return response.data;
        },
        getNextPageParam: (lastPage) => {
            if (lastPage.last) return undefined;
            return lastPage.number + 1;
        },
        initialPageParam: 0,  // <-- ajouté pour satisfaire TypeScript
    });
};

export const useGetPlayersList = () => {
    return useQuery<Player[], ApiError>({
        queryKey: [QUERY_CACHE.PLAYERS_LIST],
        queryFn: async () => {
            const response = await apiClient.get<Player[]>(`${ENDPOINTS.players}`);
            return response.data;
        },
        enabled: true,
    });
};

export const useSearchPlayers = (term: string) => {
    return useQuery<Player[], ApiError>({
        queryKey: ['searchPlayers', term],
        queryFn: async () => {
            const response = await apiClient.get<Player[]>(`${ENDPOINTS.searchPlayersByTerm(term)}`);
            return response.data;
        },
        enabled: term.length >=3,
    });
};